import { JSX } from "react";
import {
  Column_ColumnPinning,
  Column_ColumnSizing,
  flexRender,
  Header_ColumnSizing,
  RowData,
  Table,
  TableFeatures,
  Table_ColumnSizing,
} from "@tanstack/react-table";
import {
  ColumnGroupBoundaries,
  EMPTY_COLUMN_GROUP_BOUNDARIES,
} from "@app/shared/virtualized-table/getColumnGroupBoundaries";

interface VirtualizedTableHeaderProps<TFeatures extends TableFeatures, TData extends RowData> {
  /** See getColumnGroupBoundaries — draws a vertical divider marking each column group's edges. */
  columnGroupBoundaries?: ColumnGroupBoundaries;
  table: Table<TFeatures, TData>;
  testIdPrefix: string;
}

/**
 * Renders both header rows (an optional group-header row spanning multi-column groups, and the
 * leaf-column row beneath it) via TanStack's header-group model. Not virtualized — header rows are
 * always rendered in full, positioned sticky-top by the parent. role="table"-family roles (not
 * role="grid") since virtualized-table consumers have no keyboard interaction — role="grid" would
 * incorrectly advertise arrow-key navigation support to assistive tech.
 *
 * Widened to at least table.getTotalSize() (matching the body's width) via min-width, not a fixed
 * width — without it, this element has no explicit width and defaults to its scrolling ancestor's
 * visible clientWidth instead of the full table content width. A pinned/sticky cell's horizontal
 * travel range is bounded by its containing block, so a too-narrow container here makes a frozen
 * column run out of room to stick partway through a horizontal scroll. min-width (rather than
 * width) additionally lets this element grow past its own content when few columns are visible
 * (getTotalSize() shrinks with each hidden column), so the table still fills its container instead
 * of leaving a blank gap, while still scrolling normally once totalSize exceeds the container.
 *
 * Non-pinned cells also get flexGrow so individual columns share that leftover space (pinned cells
 * stay fixed — their sticky `left` offset is computed from fixed prior-column widths, so growing
 * them would desync the offset math). flexGrow uses `header.colSpan` rather than a flat `1`: a
 * group header's colSpan equals the number of leaf columns it spans, so its growth exactly matches
 * the summed growth of those leaves in the row below (each leaf's own flexGrow is 1) — keeping the
 * group header's edges aligned with its children instead of drifting apart as they grow.
 *
 * The `as typeof x & Feature_Xyz` casts below are load-bearing, not incidental: v9's per-instance
 * `Table<TFeatures, TData>`/`Header<TFeatures, TData, TValue>` generics can only resolve
 * feature-contributed methods (`getTotalSize`, `getIsPinned`, `getStart`, `getSize`, …) when
 * `TFeatures` is a concrete registry type (e.g. `typeof features`) — for an abstract `TFeatures`
 * type *parameter* like this component's, TypeScript's `ExtractFeatureMapTypes` can't statically
 * prove which feature keys are present, no matter how `TFeatures` is constrained (confirmed against
 * `node_modules/@tanstack/table-core/skills/typescript/SKILL.md`'s own "threading internal feature
 * generics manually" pitfall). Casting to the specific, mostly-non-generic feature-contribution
 * interfaces sidesteps that resolution instead of collapsing this component's `table`/`header` prop
 * to `any` — every caller still runs through `columnSizingFeature`/`columnPinningFeature`, the
 * features this component actually depends on.
 */
export const VirtualizedTableHeader = <TFeatures extends TableFeatures, TData extends RowData>({
  columnGroupBoundaries = EMPTY_COLUMN_GROUP_BOUNDARIES,
  table,
  testIdPrefix,
}: VirtualizedTableHeaderProps<TFeatures, TData>): JSX.Element => {
  const sizedTable = table as typeof table & Table_ColumnSizing;
  const totalSize = sizedTable.getTotalSize();
  return (
    <div className="virtualized-table__header-row-group" role="rowgroup" style={{ minWidth: totalSize }}>
      {table.getHeaderGroups().map((headerGroup, rowIndex) => {
        let leafColumnOffset = 0;
        return (
          <div key={headerGroup.id} className="virtualized-table__header-row" role="row" aria-rowindex={rowIndex + 1}>
            {headerGroup.headers.map((header) => {
              const colIndex = leafColumnOffset;
              leafColumnOffset += header.colSpan;
              const pinnedColumn = header.column as typeof header.column & Column_ColumnPinning & Column_ColumnSizing;
              const sizedHeader = header as typeof header & Header_ColumnSizing;
              const isPinned = pinnedColumn.getIsPinned() === "start";
              // A group-header cell (row 0, multi-leaf) IS the whole group visually, so it gets both
              // edges. A leaf cell (row 1, or a row-0 placeholder for an ungrouped column) only gets
              // an edge if it's specifically the first/last visible leaf of some group.
              const isGroupSpan = header.column.getLeafColumns().length > 1;
              const isGroupStart = isGroupSpan || columnGroupBoundaries.groupStartIds.has(header.column.id);
              const isGroupEnd = isGroupSpan || columnGroupBoundaries.groupEndIds.has(header.column.id);
              return (
                <div
                  key={header.id}
                  className={`virtualized-table__header-cell${
                    rowIndex === 0 ? " virtualized-table__header-cell--group" : ""
                  }${isPinned ? " virtualized-table__header-cell--pinned" : ""}${
                    isGroupStart ? " virtualized-table__header-cell--group-start" : ""
                  }${isGroupEnd ? " virtualized-table__header-cell--group-end" : ""}`}
                  style={{
                    flexGrow: isPinned ? 0 : header.colSpan,
                    left: isPinned ? pinnedColumn.getStart("start") : undefined,
                    width: sizedHeader.getSize(),
                  }}
                  data-testid={`${testIdPrefix}-header-${header.id}`}
                  role="columnheader"
                  aria-colindex={colIndex + 1}
                  aria-colspan={header.colSpan > 1 ? header.colSpan : undefined}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
